import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UserManager, type User, WebStorageStateStore, InMemoryWebStorage } from 'oidc-client-ts'
import { z } from 'zod'
import { CognitoClaimsSchema, type AuthProfile } from '@random-vinyl/backend/schemas'
import { log } from '@/lib/logger'

/**
 * Auth store. Wraps oidc-client-ts. The id_token and refresh_token are
 * managed inside the UserManager's storage; we expose only what the app needs.
 *
 * IMPORTANT: do not mirror tokens into Pinia state.
 *
 * Tokens live in an in-memory store (NOT sessionStorage/localStorage): a
 * persisted refresh_token is a long-lived credential readable by any XSS, which
 * the security checklist forbids. The tradeoff is that tokens do not survive a
 * full page reload — rehydration then relies on Cognito's session via
 * `signinSilent` (automaticSilentRenew), so verify that silent-renew flow when
 * touching this file.
 */

let _userManager: UserManager | null = null
function userManager(): UserManager {
  if (_userManager) return _userManager
  _userManager = new UserManager({
    authority: import.meta.env.VITE_COGNITO_AUTHORITY,
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirect_uri: import.meta.env.VITE_COGNITO_REDIRECT_URI,
    post_logout_redirect_uri: import.meta.env.VITE_COGNITO_POST_LOGOUT_URI,
    response_type: 'code',
    scope: 'openid email profile',
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    automaticSilentRenew: true,
    monitorSession: false,
  })
  return _userManager
}

/** What `login()` puts into the oidc-client-ts signin state. */
const LoginStateSchema = z.object({ returnTo: z.string().optional() })

export type { AuthProfile }

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null && !user.value.expired)
  // Validate Cognito claims via zod (single source of truth lives in
  // `@random-vinyl/backend/schemas`). Both branches of this computed feed the
  // sibling computeds below; we don't re-parse from each one.
  const claimResult = computed(() =>
    user.value ? CognitoClaimsSchema.safeParse(user.value.profile) : null
  )
  const profile = computed<AuthProfile | null>(() => {
    const r = claimResult.value
    return r && r.success ? r.data : null
  })
  /**
   * True when a user is present but its claims fail validation (role outside
   * the enum, tenant_id not matching the `tnt-…` format, missing `sub`).
   * UIs can watch this to render an `ERR_INVALID_CLAIMS` banner; the value
   * is derived purely from `user.value`, so there is no in-computed mutation.
   */
  const hasInvalidClaims = computed(() => claimResult.value !== null && !claimResult.value.success)
  const role = computed(() => profile.value?.role ?? null)
  const isAdmin = computed(() => role.value === 'admin')

  /**
   * Best-effort silent rehydrate from the in-memory user store. After a full
   * page reload this store is empty (tokens are not persisted), so getUser()
   * returns null and the router fallback re-runs `login()` (which leans on the
   * Cognito session for a seamless redirect). Failures are logged but
   * intentionally NOT surfaced to `error` — the login fallback owns the
   * user-facing error code; surfacing here would race with that flow.
   */
  async function loadFromStorage() {
    try {
      const u = await userManager().getUser()
      user.value = u
    } catch (err: unknown) {
      log.warn('[auth] loadFromStorage failed', err)
    }
  }

  async function getIdToken(): Promise<string | null> {
    if (!user.value || user.value.expired) {
      try {
        const refreshed = await userManager().signinSilent()
        user.value = refreshed
      } catch {
        // Silent renew failed — clear the expired/stale user so derived state
        // (profile, role, hasInvalidClaims) doesn't keep reflecting a dead
        // session until handleAuthFailure eventually runs.
        user.value = null
        return null
      }
    }
    return user.value?.id_token ?? null
  }

  /**
   * Redirect to the Hosted UI. `returnTo` is the in-app path to come back to;
   * it rides in the signin `state`, which oidc-client-ts keeps in its own
   * stateStore (localStorage by default — only `userStore` is overridden
   * above) next to the PKCE verifier, so only the state's random id travels
   * through Cognito. `completeLogin()` hands it back.
   */
  async function login(returnTo?: string) {
    loading.value = true
    error.value = null
    try {
      await userManager().signinRedirect({ state: { returnTo } })
    } catch (err: unknown) {
      loading.value = false
      error.value = 'ERR_LOGIN_FAILED'
      throw err
    }
  }

  /**
   * Finish the code exchange. Resolves to the `returnTo` that `login()` stored
   * in the signin state, or null when there is none. It is NOT validated here:
   * the caller runs it through `safeReturnTo` (lib/return-to.ts) before
   * navigating.
   */
  async function completeLogin(): Promise<string | null> {
    loading.value = true
    error.value = null
    try {
      const u = await userManager().signinRedirectCallback()
      user.value = u
      // Surface invalid/missing claims on the same error channel as other auth
      // failures, instead of relying solely on the derived `hasInvalidClaims`.
      // A user with a malformed tenant_id/role would otherwise "log in"
      // successfully and the callback would navigate into the app.
      if (!CognitoClaimsSchema.safeParse(u.profile).success) {
        throw new Error('ERR_INVALID_CLAIMS')
      }
      const state = LoginStateSchema.safeParse(u.state)
      return state.success ? (state.data.returnTo ?? null) : null
    } catch (err: unknown) {
      // Derive the code from the error itself (not a prior `error.value`
      // assignment) so this can't silently downgrade if the code above is
      // refactored: a claims failure keeps ERR_INVALID_CLAIMS, anything else
      // (the signinRedirectCallback rejection) reports the generic failure.
      error.value =
        err instanceof Error && err.message === 'ERR_INVALID_CLAIMS'
          ? 'ERR_INVALID_CLAIMS'
          : 'ERR_LOGIN_CALLBACK_FAILED'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    error.value = null
    try {
      // Reset all other stores BEFORE clearing auth, so derived state has a
      // chance to settle. Dynamic import dodges a static cycle: the registry
      // imports every store, and every store would transitively import the
      // registry through the auth store if this were a top-level import.
      const { resetAllStores } = await import('./reset-registry')
      await resetAllStores()

      // Capture the id_token before removing the user — Cognito's /logout doesn't
      // require id_token_hint today (client_id + logout_uri are enough), but a
      // future federated IdP will need it to bind the logout to the user, and
      // Cognito tolerates the extra param. Forward it when available.
      const idToken = user.value?.id_token ?? null

      // Clear oidc-client-ts's stored user (in-memory store). We don't call
      // userManager().signoutRedirect() because that builds an OIDC-standard
      // end_session request (id_token_hint + post_logout_redirect_uri), and
      // Cognito's /logout endpoint expects client_id + logout_uri.
      // Build the URL by hand against Cognito's documented contract.
      await userManager().removeUser()
      user.value = null

      const base = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN
      const url = new URL('/logout', base)
      url.searchParams.set('client_id', import.meta.env.VITE_COGNITO_CLIENT_ID)
      url.searchParams.set('logout_uri', import.meta.env.VITE_COGNITO_POST_LOGOUT_URI)
      if (idToken) url.searchParams.set('id_token_hint', idToken)
      window.location.href = url.toString()
    } catch (err: unknown) {
      // Even if oidc-client-ts couldn't remove the user from sessionStorage
      // (corrupt blob etc.), clear the in-memory ref so the UI reflects a
      // logged-out state.
      user.value = null
      // The primary sign-out is a full-page redirect to Cognito's /logout. If
      // that couldn't run (e.g. a missing VITE_COGNITO_HOSTED_UI_DOMAIN made
      // `new URL('/logout', base)` throw), the user would be stranded on the
      // now-unauthenticated protected view with no navigation. Fall back to a
      // login redirect so they still land on the sign-in screen rather than
      // staying on the page.
      //
      // Caveat: we reach this branch precisely because /logout did NOT run, so
      // Cognito's hosted-UI SSO cookie is still live. signinRedirect() may then
      // complete silently against that session and re-log the user straight
      // back in — i.e. this "logout" can re-authenticate. That's an accepted
      // best-effort: in the documented failure (missing hosted-UI domain) the
      // session would persist regardless, and getting the user off the
      // protected page beats leaving them stranded. There is no neutral public
      // route to bounce to (every app route requires auth), so the sign-in
      // redirect is the safest available exit.
      try {
        await login()
      } catch (loginErr: unknown) {
        log.error('[auth] logout fallback login redirect failed', loginErr)
      }
      // Set the logout error code AFTER the fallback: login() clears error on
      // entry, so assigning before would be wiped. This is the code surfaced
      // when neither the redirect nor the fallback navigated away.
      error.value = 'ERR_LOGOUT_FAILED'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function handleAuthFailure() {
    user.value = null
    try {
      // Come back to the page the 401 interrupted (validated by the callback).
      const { pathname, search, hash } = new URL(window.location.href)
      await login(pathname + search + hash)
    } catch (err: unknown) {
      // login() already sets error to ERR_LOGIN_FAILED before rethrowing;
      // log here so the failure leaves a trail even when prod-mode logger
      // is a no-op below.
      log.error('[auth] handleAuthFailure: login redirect failed', err)
    }
  }

  function reset() {
    user.value = null
    loading.value = false
    error.value = null
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    profile,
    hasInvalidClaims,
    role,
    isAdmin,
    loadFromStorage,
    getIdToken,
    login,
    completeLogin,
    logout,
    handleAuthFailure,
    reset,
  }
})
